import {
    Component,
    OnInit,
    OnChanges,
    SimpleChanges,
    Input,
    Output,
    EventEmitter,
    inject,
    signal,
    OnDestroy,
    computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Button } from 'primeng/button';
import { OrganizationService } from '../../../core/services/organization.service';
import { EntityCacheService } from '../../../core/services/entity-cache.service';
import { ClientRecordsService } from '../../../core/services/client-records.service';
import { OrganizationResponseDto } from '@qc/shared';
import { OrganizationFormComponent } from './organization-form.component';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';
import { ContactWithRole } from '../../interfaces/contact.interface';
import { FormValidationDetails } from '@/app/features/clients/components/cir-forms/core/form-section.interface';

@Component({
    selector: 'qc-organization-selector',
    standalone: true,
    imports: [CommonModule, FormsModule, InputTextModule, SelectModule, Button, OrganizationFormComponent],
    template: `
        <div class="form-field">
            <div
                *ngIf="!useTaggedDisplay && !selectedOrganization() && !showInlineForm() && !showInlineEditForm()"
                class="space-y-3"
            >
                <label class="block text-sm mb-1">
                    {{ label }}
                    <span *ngIf="required">*</span>
                </label>
                <p-select
                    [options]="filteredOrganizations()"
                    [(ngModel)]="selectedOrganization"
                    optionLabel="organizationName"
                    placeholder="Select an organization..."
                    (onChange)="onDropdownChange($event)"
                    (onShow)="onDropdownShow()"
                    [showClear]="true"
                    [filter]="true"
                    filterBy="organizationName"
                    (onFilter)="onSearchChange($event.filter)"
                    styleClass="w-full"
                    panelStyleClass="dropdown-panel"
                    scrollHeight="200px"
                    [class.border-rose-500]="hasValidationError()"
                    [disabled]="disabled"
                    [loading]="isSearching()"
                >
                    <ng-template let-organization pTemplate="item">
                        <div class="p-3">
                            <div class="font-medium text-gray-900">{{ organization.organizationName }}</div>
                            <div class="org-address text-sm text-gray-500 mt-1 break-words whitespace-normal" *ngIf="organization.address">
                                {{ organization.address }}
                            </div>
                        </div>
                    </ng-template>
                </p-select>
                <div *ngIf="showCreateButton" class="text-center mt-3">
                    <p-button
                        severity="secondary"
                        variant="outlined"
                        (click)="openCreateModal()"
                        icon="pi pi-plus"
                        label="Add New Organization"
                        size="small"
                        [disabled]="disabled"
                    />
                </div>
            </div>

            <div
                *ngIf="useTaggedDisplay && !selectedOrganization() && !showInlineForm() && !showInlineEditForm()"
                class="space-y-3"
            >
                <label class="block text-sm mb-1">
                    {{ label }}
                    <span *ngIf="required">*</span>
                </label>
                <p-select
                    [options]="filteredOrganizations()"
                    [(ngModel)]="selectedOrganization"
                    optionLabel="organizationName"
                    placeholder="Select an organization..."
                    (onChange)="onDropdownChange($event)"
                    (onShow)="onDropdownShow()"
                    [showClear]="true"
                    [filter]="true"
                    filterBy="organizationName"
                    (onFilter)="onSearchChange($event.filter)"
                    styleClass="w-full"
                    panelStyleClass="dropdown-panel"
                    scrollHeight="200px"
                    [class.border-rose-500]="hasValidationError()"
                    [disabled]="disabled"
                    [loading]="isSearching()"
                >
                    <ng-template let-organization pTemplate="item">
                        <div class="p-3">
                            <div class="font-medium text-gray-900">{{ organization.organizationName }}</div>
                            <div class="org-address text-sm text-gray-500 mt-1 break-words whitespace-normal" *ngIf="organization.address">
                                {{ organization.address }}
                            </div>
                        </div>
                    </ng-template>
                </p-select>
                <div *ngIf="showCreateButton" class="text-center mt-3">
                    <p-button
                        severity="secondary"
                        variant="outlined"
                        (click)="openCreateModal()"
                        icon="pi pi-plus"
                        label="Add New Organization"
                        size="small"
                        [disabled]="disabled"
                    />
                </div>
            </div>

            <div *ngIf="useTaggedDisplay && selectedOrganization() && !showInlineEditForm()" class="space-y-3">
                <label class="block text-sm mb-1">
                    {{ label }}
                    <span *ngIf="required">*</span>
                </label>
                <div class="flex justify-between items-start border border-gray-200 rounded-md p-3">
                    <div>
                        <div class="font-medium text-gray-900">{{ selectedOrganization()?.organizationName }}</div>
                        <div class="text-sm text-gray-500 mt-1" *ngIf="selectedOrganization()?.address">
                            {{ selectedOrganization()?.address }}
                        </div>
                    </div>
                    <p-button
                        severity="secondary"
                        variant="text"
                        (click)="clearSelection()"
                        icon="pi pi-times"
                        size="small"
                        [disabled]="disabled"
                    />
                </div>
            </div>
            <div *ngIf="!useTaggedDisplay && selectedOrganization() && !showInlineEditForm()">
                <div class="flex justify-between items-start">
                    <div class="flex-1">
                        <div class="mb-4">
                            <div class="block text-sm mb-1 opacity-50">Organization Name</div>
                            {{ selectedOrganization()?.organizationName }}
                        </div>
                        <div class="mb-4">
                            <div class="block text-sm mb-1 opacity-50">Organization Type</div>
                            {{ selectedOrganization()?.organizationType }}
                        </div>
                        <div class="mb-4" *ngIf="selectedOrganization()?.address">
                            <div class="block text-sm mb-1 opacity-50">Address</div>
                            {{ selectedOrganization()?.address }}
                        </div>
                    </div>
                    <div class="flex gap-2">
                        <p-button
                            severity="secondary"
                            variant="outlined"
                            (click)="openInlineEdit()"
                            icon="pi pi-pencil"
                            label="Edit"
                            size="small"
                            [disabled]="disabled"
                        />
                    </div>
                </div>

                <div
                    *ngIf="
                        selectedOrganization()?.delNumber ||
                        selectedOrganization()?.nhpSiteLicence ||
                        selectedOrganization()?.mdelNumber ||
                        selectedOrganization()?.dealersLicence
                    "
                    class="border-t border-t-gray-200 pt-6 mt-3"
                >
                    <h4 class="text-lg font-semibold mb-3">Licenses</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div *ngIf="selectedOrganization()?.delNumber">
                            <span class="opacity-50">DEL:</span> {{ selectedOrganization()?.delNumber }}
                        </div>
                        <div *ngIf="selectedOrganization()?.nhpSiteLicence">
                            <span class="opacity-50">NHP Site:</span> {{ selectedOrganization()?.nhpSiteLicence }}
                        </div>
                        <div *ngIf="selectedOrganization()?.mdelNumber">
                            <span class="opacity-50">MDEL:</span> {{ selectedOrganization()?.mdelNumber }}
                        </div>
                        <div *ngIf="selectedOrganization()?.dealersLicence">
                            <span class="opacity-50">Dealer's:</span> {{ selectedOrganization()?.dealersLicence }}
                        </div>
                    </div>
                </div>

                <div *ngIf="selectedOrganization()?.sampleRetentionAnnex" class="border-t border-t-gray-200 mt-6 pt-6">
                    <h4 class="text-lg font-semibold mb-3">Sample Retention</h4>
                    <div class="mb-4">
                        <div class="text-sm mb-1 opacity-50">Annex</div>
                        {{ selectedOrganization()?.sampleRetentionAnnex }}
                    </div>
                    <div class="text-sm mb-1 opacity-50">Comments</div>
                    <div *ngIf="selectedOrganization()?.sampleRetentionComments" class="mt-1">
                        {{ selectedOrganization()?.sampleRetentionComments }}
                    </div>
                </div>

                <div
                    *ngIf="
                        selectedOrganization()?.organizationType === 'TESTER_RELEASE' &&
                        (selectedOrganization()?.testingServiceApi ||
                            selectedOrganization()?.testingServiceId ||
                            selectedOrganization()?.testingServiceConfirmatory ||
                            selectedOrganization()?.specialLabNotes ||
                            selectedOrganization()?.lotReleaseBlockedUntilConfirmatory !== undefined ||
                            selectedOrganization()?.lotReleaseComments)
                    "
                    class="border-t border-t-gray-200 mt-6 pt-6"
                >
                    <h4 class="text-lg font-semibold mb-3">Testing Service Information</h4>
                    <div
                        *ngIf="
                            selectedOrganization()?.testingServiceApi ||
                            selectedOrganization()?.testingServiceId ||
                            selectedOrganization()?.testingServiceConfirmatory
                        "
                        class="mb-3"
                    >
                        <div class="text-sm mb-1 opacity-50">Testing Service Types</div>
                        <div class="flex gap-1">
                            <span
                                *ngIf="selectedOrganization()?.testingServiceApi"
                                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-lime-500 text-white"
                            >
                                API
                            </span>
                            <span
                                *ngIf="selectedOrganization()?.testingServiceId"
                                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-lime-500 text-white"
                            >
                                ID
                            </span>
                            <span
                                *ngIf="selectedOrganization()?.testingServiceConfirmatory"
                                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-lime-500 text-white"
                            >
                                Confirmatory
                            </span>
                        </div>
                    </div>

                    <div *ngIf="selectedOrganization()?.specialLabNotes" class="mb-3">
                        <div class="text-sm mb-1 opacity-50">Special Laboratory Notes</div>
                        <div class="whitespace-pre-line">{{ selectedOrganization()?.specialLabNotes }}</div>
                    </div>

                    <div *ngIf="selectedOrganization()?.lotReleaseBlockedUntilConfirmatory !== undefined" class="mb-3">
                        <div class="text-sm mb-1 opacity-50">Lot Release Blocked Until Confirmatory</div>
                        {{ selectedOrganization()?.lotReleaseBlockedUntilConfirmatory ? 'Yes' : 'No' }}
                    </div>

                    <div *ngIf="selectedOrganization()?.lotReleaseComments" class="mb-3">
                        <div class="text-sm mb-1 opacity-50">Lot Release Comments</div>
                        <div class="whitespace-pre-line">{{ selectedOrganization()?.lotReleaseComments }}</div>
                    </div>
                </div>

                <div class="border-t border-t-gray-200 mt-6 pt-6">
                    <h4 class="text-lg font-semibold mb-3">Contacts</h4>
                    <div *ngIf="organizationContacts().length > 0" class="space-y-2">
                        <div
                            *ngFor="let contact of organizationContacts()"
                            class="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded"
                        >
                            <div class="flex-1">
                                <div class="font-medium text-gray-900">{{ contact.fullName }}</div>
                                <div class="text-sm text-gray-500" *ngIf="contact.email">{{ contact.email }}</div>
                                <div *ngIf="contact.roleDescription" class="text-sm text-blue-600 mt-1">
                                    {{ contact.roleDescription }}
                                </div>
                                <div *ngIf="contact.context" class="text-sm text-gray-400 mt-1">
                                    {{ contact.context }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div *ngIf="organizationContacts().length === 0" class="text-center py-4 text-gray-500 text-sm">
                        No contacts assigned to this organization.
                    </div>
                </div>

                <div *ngIf="selectedOrganization()?.notes" class="border-t border-t-gray-200 mt-6 pt-6">
                    <div class="text-sm mb-1 opacity-50">Notes</div>
                    <div class="whitespace-pre-line">{{ selectedOrganization()?.notes }}</div>
                </div>
            </div>

            <div *ngIf="hasValidationError()" class="text-rose-500 text-sm mt-1">
                {{ getValidationError() }}
            </div>
        </div>

        <qc-organization-form
            *ngIf="showInlineForm()"
            mode="create"
            [organization]="null"
            [showContactsManager]="true"
            [deferSave]="deferSave"
            (organizationDataWithContacts)="onOrganizationCreatedWithContacts($event)"
            (cancel)="onOrganizationCreateCancel()"
        />

        <qc-organization-form
            *ngIf="showInlineEditForm()"
            mode="edit"
            [organization]="selectedOrganization()"
            [organizationId]="selectedOrganization()?.id ?? null"
            [showContactsManager]="true"
            [deferSave]="deferSave"
            (save)="onOrganizationUpdated($event)"
            (cancel)="onOrganizationEditCancel()"
        />
    `,
    styles: [
        `
            :host ::ng-deep .dropdown-panel {
                max-width: min(90vw, 520px);
            }

            :host ::ng-deep .dropdown-panel .p-select-option {
                white-space: normal;
            }

            :host ::ng-deep .dropdown-panel .org-address {
                overflow-wrap: anywhere;
                word-break: break-word;
            }
        `,
    ],
})

export class OrganizationSelectorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() label: string = 'Organization';
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() showCreateButton: boolean = true;
    @Input() useInlineForm: boolean = false;
    @Input() initialValue: Partial<OrganizationResponseDto> | null = null;
    @Input() useTaggedDisplay: boolean = false;
    @Input() excludedOrganizationIds: string[] = [];
    @Input() deferSave: boolean = false; // Pass through to organization-form
    @Output() organizationSelected = new EventEmitter<OrganizationResponseDto>();
    @Output() organizationCleared = new EventEmitter<void>();
    @Output() organizationCreated = new EventEmitter<OrganizationResponseDto>();
    @Output() organizationUpdated = new EventEmitter<OrganizationResponseDto>();
    @Output() organizationCreatedWithContacts = new EventEmitter<{
        organization: OrganizationResponseDto;
        contacts: ContactWithRole[];
    }>();
    @Output() validationChange = new EventEmitter<boolean>();
    @Output() validationDetailsChange = new EventEmitter<FormValidationDetails>();
    @Output() createModeChange = new EventEmitter<boolean>();

    private organizationService = inject(OrganizationService);
    private entityCacheService = inject(EntityCacheService);
    private clientRecordsService = inject(ClientRecordsService);
    private destroy$ = new Subject<void>();
    private searchTerms$ = new BehaviorSubject<string>('');

    selectedOrganization = signal<OrganizationResponseDto | null>(null);
    allOrganizations = signal<OrganizationResponseDto[]>([]);
    filteredOrganizations = signal<OrganizationResponseDto[]>([]);
    organizationContacts = signal<ContactWithRole[]>([]);
    isSearching = signal<boolean>(false);
    showInlineForm = signal<boolean>(false);
    showInlineEditForm = signal<boolean>(false);
    requiredSignal = signal<boolean>(false);
    touched = signal<boolean>(false);

    private loadedOrganizationId: string | null = null;
    private loadedOrganizationContactsId: string | null = null;
    private loadingOrganizationContactsId: string | null = null;

    private extractContactsFromOrganization(organization: any): ContactWithRole[] | null {
        if (!organization) return null;

        const candidate = organization.organizationContacts ?? organization.contacts ?? null;
        if (!candidate) return null;

        if (Array.isArray(candidate)) {
            return (candidate as ContactWithRole[]) || [];
        }

        const candidateAny = candidate as any;
        if (Array.isArray(candidateAny.items)) {
            return (candidateAny.items as ContactWithRole[]) || [];
        }

        if (candidateAny.data && Array.isArray(candidateAny.data.items)) {
            return (candidateAny.data.items as ContactWithRole[]) || [];
        }

        if (candidateAny.data && Array.isArray(candidateAny.data)) {
            return (candidateAny.data as ContactWithRole[]) || [];
        }

        return null;
    }

    isValid = computed(() => {
        if (!this.required) return true;
        return !!this.selectedOrganization();
    });

    ngOnInit(): void {
        this.setupSearch();
        this.handleInitialValue();

        const validateAllHandler = () => {
            this.markAsTouched();
        };
        document.addEventListener('validateAllForms', validateAllHandler as EventListener);
        this.destroy$.subscribe(() => {
            document.removeEventListener('validateAllForms', validateAllHandler as EventListener);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialValue']) {
            this.handleInitialValue();
        }
        if (changes['required']) {
            this.emitValidation();
        }
        if (changes['excludedOrganizationIds']) {
            this.loadInitialOrganizations();
        }
    }

    private handleInitialValue(): void {
        if (this.initialValue && typeof this.initialValue === 'object') {
            if (this.initialValue.organizationName) {
                this.selectedOrganization.set(this.initialValue as OrganizationResponseDto);
                // Load contacts only if they will be displayed (not in tagged mode)
                if (this.initialValue.id && !this.useTaggedDisplay) {
                    const contactsFromInitialValue = this.extractContactsFromOrganization(this.initialValue);

                    if (contactsFromInitialValue !== null) {
                        this.organizationContacts.set(contactsFromInitialValue || []);
                        this.loadedOrganizationContactsId = this.initialValue.id;
                        this.loadingOrganizationContactsId = null;
                    } else {
                        this.loadOrganizationContacts(this.initialValue.id);
                    }
                }
            } else if (this.initialValue.id && this.loadedOrganizationId !== this.initialValue.id) {
                this.loadOrganizationById(this.initialValue.id);
            }
        } else {
            this.selectedOrganization.set(null);
            this.organizationContacts.set([]);
            this.loadedOrganizationId = null;
            this.loadedOrganizationContactsId = null;
            this.loadingOrganizationContactsId = null;
        }
        this.emitValidation();
    }

    private loadOrganizationById(id: string): void {
        this.loadedOrganizationId = id;
        this.organizationService
            .getOrganization(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (organization) => {
                    this.selectedOrganization.set(organization);
                    if (!this.useTaggedDisplay) {
                        const contactsFromOrganization = this.extractContactsFromOrganization(organization);

                        if (contactsFromOrganization !== null) {
                            this.organizationContacts.set(contactsFromOrganization || []);
                            this.loadedOrganizationContactsId = organization.id;
                            this.loadingOrganizationContactsId = null;
                        } else {
                            this.loadOrganizationContacts(organization.id);
                        }
                    }
                    this.emitValidation();
                },
                error: (error) => {
                    this.selectedOrganization.set(null);
                    this.organizationContacts.set([]);
                    this.loadedOrganizationId = null;
                    this.emitValidation();
                },
            });
    }

    private filterExcludedOrganizations(organizations: OrganizationResponseDto[]): OrganizationResponseDto[] {
        if (!this.excludedOrganizationIds || this.excludedOrganizationIds.length === 0) {
            return organizations;
        }
        return organizations.filter((org) => !this.excludedOrganizationIds.includes(org.id));
    }

    private setupSearch(): void {
        this.searchTerms$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((searchTerm) => {
                    this.isSearching.set(true);
                    return this.entityCacheService.getOrganizations(searchTerm, 1, 100);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (result) => {
                    this.filteredOrganizations.set(this.filterExcludedOrganizations(result.items));
                    this.isSearching.set(false);
                },
                error: (error) => {
                    this.isSearching.set(false);
                },
            });
    }

    private loadInitialOrganizations(): void {
        this.entityCacheService
            .getOrganizations('', 1, 100)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.allOrganizations.set(this.filterExcludedOrganizations(result.items));
                    this.filteredOrganizations.set(this.filterExcludedOrganizations(result.items));
                },
                error: (error) => { },
            });
    }

    onSearchChange(searchTerm: string): void {
        this.searchTerms$.next(searchTerm);
    }

    onDropdownShow(): void {
        if (this.allOrganizations().length === 0) {
            this.loadInitialOrganizations();
        }
    }

    onDropdownChange(event: any): void {
        this.touched.set(true);
        const organization = event.value;
        if (organization) {
            this.selectedOrganization.set(organization);
            if (!this.useTaggedDisplay) {
                const organizationAny = organization as any;
                const contactsFromOrganization =
                    (Array.isArray(organizationAny.organizationContacts) && organizationAny.organizationContacts) ||
                    (Array.isArray(organizationAny.contacts) && organizationAny.contacts) ||
                    null;

                if (contactsFromOrganization) {
                    this.organizationContacts.set((contactsFromOrganization as ContactWithRole[]) || []);
                    this.loadedOrganizationContactsId = organization.id;
                    this.loadingOrganizationContactsId = null;
                } else {
                    this.loadOrganizationContacts(organization.id);
                }
            }
            this.organizationSelected.emit(organization);
        } else {
            this.selectedOrganization.set(null);
            this.organizationContacts.set([]);
            this.organizationCleared.emit();
        }
        this.emitValidation();
    }

    clearSelection(): void {
        this.touched.set(true);
        this.selectedOrganization.set(null);
        this.organizationContacts.set([]);
        this.loadedOrganizationContactsId = null;
        this.loadingOrganizationContactsId = null;
        this.organizationCleared.emit();
        this.emitValidation();
    }

    openCreateModal(): void {
        if (this.useInlineForm) {
            this.showInlineForm.set(true);
            this.createModeChange.emit(true);
        }
    }

    onOrganizationCreateCancel(): void {
        this.showInlineForm.set(false);
        this.createModeChange.emit(false);
    }

    openInlineEdit(): void {
        this.showInlineEditForm.set(true);
    }

    onOrganizationCreatedWithContacts(data: {
        organization: OrganizationResponseDto;
        contacts: ContactWithRole[];
    }): void {
        this.allOrganizations.update((orgs) => [...orgs, data.organization]);
        this.filteredOrganizations.update((orgs) => [...orgs, data.organization]);
        this.selectedOrganization.set(data.organization);
        this.organizationContacts.set(data.contacts || []);
        this.organizationSelected.emit(data.organization);
        this.organizationCreatedWithContacts.emit(data);
        this.showInlineForm.set(false);
        this.createModeChange.emit(false);
        this.entityCacheService.invalidateOrganizationsCache();
        this.emitValidation();
    }

    onOrganizationUpdated(updatedOrganization: OrganizationResponseDto): void {
        this.allOrganizations.update((orgs) =>
            orgs.map((org) => (org.id === updatedOrganization.id ? updatedOrganization : org)),
        );
        this.filteredOrganizations.update((orgs) =>
            orgs.map((org) => (org.id === updatedOrganization.id ? updatedOrganization : org)),
        );
        this.selectedOrganization.set(updatedOrganization);
        const updatedOrganizationAny = updatedOrganization as any;
        const contactsFromOrganization =
            (Array.isArray(updatedOrganizationAny.organizationContacts) && updatedOrganizationAny.organizationContacts) ||
            (Array.isArray(updatedOrganizationAny.contacts) && updatedOrganizationAny.contacts) ||
            null;

        if (contactsFromOrganization) {
            this.organizationContacts.set((contactsFromOrganization as ContactWithRole[]) || []);
            this.loadedOrganizationContactsId = updatedOrganization.id;
            this.loadingOrganizationContactsId = null;
        } else {
            this.loadOrganizationContacts(updatedOrganization.id);
        }
        this.organizationSelected.emit(updatedOrganization);
        this.organizationUpdated.emit(updatedOrganization);
        this.showInlineEditForm.set(false);
        this.entityCacheService.invalidateOrganizationsCache();
        this.emitValidation();
    }

    onOrganizationEditCancel(): void {
        this.showInlineEditForm.set(false);
    }

    hasValidationError(): boolean {
        return this.required && !this.selectedOrganization() && this.touched();
    }

    getValidationError(): string {
        if (!this.hasValidationError()) return '';
        return 'Please select an organization from the list or create a new one';
    }

    markAsTouched(): void {
        this.touched.set(true);
        this.emitValidation();
    }

    private emitValidation(): void {
        const details = this.buildValidationDetails();
        this.validationChange.emit(details.isValid);
        this.validationDetailsChange.emit(details);
    }

    private buildValidationDetails(): FormValidationDetails {
        if (!this.required) {
            return { isValid: true, errors: [] };
        }

        if (this.selectedOrganization()) {
            return { isValid: true, errors: [] };
        }

        if (!this.touched()) {
            return { isValid: false, errors: [] };
        }

        return {
            isValid: false,
            errors: [
                {
                    field: 'organization',
                    message: this.getValidationError(),
                },
            ],
        };
    }

    private loadOrganizationContacts(organizationId: string): void {
        const selected = this.selectedOrganization();
        const embeddedContacts = this.extractContactsFromOrganization(selected as any);
        if (selected?.id === organizationId && embeddedContacts !== null) {
            this.organizationContacts.set(embeddedContacts || []);
            this.loadedOrganizationContactsId = organizationId;
            this.loadingOrganizationContactsId = null;

            const orgWithContacts = {
                ...(selected as any),
                organizationContacts: embeddedContacts || [],
            } as OrganizationResponseDto;
            this.selectedOrganization.set(orgWithContacts);
            this.organizationSelected.emit(orgWithContacts);
            return;
        }

        if (this.loadedOrganizationContactsId === organizationId) {
            return;
        }

        if (this.loadingOrganizationContactsId === organizationId) {
            return;
        }

        this.loadingOrganizationContactsId = organizationId;

        this.entityCacheService
            .getOrganizationContacts(organizationId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (contacts) => {
                    if (this.selectedOrganization()?.id !== organizationId) {
                        if (this.loadingOrganizationContactsId === organizationId) {
                            this.loadingOrganizationContactsId = null;
                        }
                        return;
                    }
                    this.organizationContacts.set((contacts as ContactWithRole[]) || []);
                    this.loadedOrganizationContactsId = organizationId;
                    if (this.loadingOrganizationContactsId === organizationId) {
                        this.loadingOrganizationContactsId = null;
                    }

                    const current = this.selectedOrganization();
                    if (current?.id === organizationId) {
                        const orgWithContacts = {
                            ...(current as any),
                            organizationContacts: (contacts as ContactWithRole[]) || [],
                        } as OrganizationResponseDto;
                        this.selectedOrganization.set(orgWithContacts);
                        this.organizationSelected.emit(orgWithContacts);
                        this.emitValidation();
                    }
                },
                error: (error) => {
                    if (this.selectedOrganization()?.id === organizationId) {
                        this.organizationContacts.set([]);
                        this.loadedOrganizationContactsId = null;
                    }
                    if (this.loadingOrganizationContactsId === organizationId) {
                        this.loadingOrganizationContactsId = null;
                    }
                },
            });

    }
}
